// Support.jsx
import { useState } from 'react'
import { Search, Plus, MessageCircle, Clock, CheckCircle2, AlertCircle, Tag, Eye, ChevronRight, X, Send } from 'lucide-react'

const TICKETS = [
  { id:'TKT-1041', user:'Sarah Mitchell',  email:'sarah@acme.com',   subject:'Cannot export report as PDF',        category:'Bug',      priority:'high',   status:'open',        date:'May 5, 2025',  msgs:3 },
  { id:'TKT-1040', user:'James Holloway',  email:'james@corp.io',    subject:'How do I add sub-admin?',            category:'Question', priority:'low',    status:'resolved',    date:'May 4, 2025',  msgs:5 },
  { id:'TKT-1039', user:'Priya Sharma',    email:'priya@start.co',   subject:'Payment failed but money deducted',  category:'Billing',  priority:'urgent', status:'in-progress', date:'May 4, 2025',  msgs:7 },
  { id:'TKT-1038', user:'Luca Martini',    email:'luca@design.eu',   subject:'Dark mode not saving preference',    category:'Bug',      priority:'medium', status:'open',        date:'May 3, 2025',  msgs:2 },
  { id:'TKT-1037', user:'Emily Chen',      email:'emily@techco.com', subject:'Request for invoice copy',           category:'Billing',  priority:'low',    status:'resolved',    date:'May 2, 2025',  msgs:4 },
  { id:'TKT-1036', user:'Daniel Okafor',   email:'daniel@ng.dev',    subject:'API rate limit too restrictive',     category:'Feature',  priority:'medium', status:'in-progress', date:'May 1, 2025',  msgs:6 },
]

const PRIORITY = {
  urgent: { cls:'badge-danger',  l:'Urgent', dot:'#EF4444' },
  high:   { cls:'badge-warning', l:'High',   dot:'#F59E0B' },
  medium: { cls:'badge-info',    l:'Medium', dot:'#06B6D4' },
  low:    { cls:'badge-active',  l:'Low',    dot:'#10B981' },
}

const STATUS = {
  open:          { cls:'badge-warning', l:'Open',        icon:AlertCircle,  color:'#F59E0B' },
  'in-progress': { cls:'badge-info',    l:'In Progress', icon:Clock,        color:'#06B6D4' },
  resolved:      { cls:'badge-active',  l:'Resolved',    icon:CheckCircle2, color:'#10B981' },
}

const INITIAL_MSGS = {
  'TKT-1041': [
    { from:'user',  text:'I am unable to export reports in PDF format. The button clicks but nothing happens.',  time:'14:02' },
    { from:'agent', text:'Thank you for reaching out! Could you please share your browser and OS version?',      time:'14:15' },
    { from:'user',  text:'Chrome 123, Windows 11. Also tried on Firefox, same issue.',                          time:'14:18' },
  ],
}

export default function Support() {
  const [tickets, setTickets] = useState(TICKETS)
  const [search, setSearch]   = useState('')
  const [filter, setFilter]   = useState('all')
  const [active, setActive]   = useState(null)
  const [reply, setReply]     = useState('')
  const [allMsgs, setAllMsgs] = useState(INITIAL_MSGS)

  const filtered = tickets.filter(t => {
    const matchStatus = filter === 'all' || t.status === filter
    const matchSearch = !search || t.subject.toLowerCase().includes(search.toLowerCase()) || t.user.toLowerCase().includes(search.toLowerCase())
    return matchStatus && matchSearch
  })

  const stats = [
    { label:'Total Tickets', value:tickets.length,                                         color:'#4F46E5', colorGlow:'rgba(79,70,229,0.18)',  icon:MessageCircle, variant:'indigo', trend:'+4' },
    { label:'Open',          value:tickets.filter(t=>t.status==='open').length,            color:'#F59E0B', colorGlow:'rgba(245,158,11,0.15)',  icon:AlertCircle,   variant:'amber',  trend:'2 new',  trendDir:'neutral' },
    { label:'In Progress',   value:tickets.filter(t=>t.status==='in-progress').length,     color:'#06B6D4', colorGlow:'rgba(6,182,212,0.15)',   icon:Clock,         variant:'cyan',   trend:'active', trendDir:'neutral' },
    { label:'Resolved',      value:tickets.filter(t=>t.status==='resolved').length,        color:'#10B981', colorGlow:'rgba(16,185,129,0.15)',  icon:CheckCircle2,  variant:'green',  trend:'+3' },
  ]

  const updateStatus = (id, status) => {
    setTickets(prev => prev.map(t => t.id === id ? { ...t, status } : t))
    setActive(prev => prev?.id === id ? { ...prev, status } : prev)
  }

  const sendReply = () => {
    if (!reply.trim() || !active) return
    const now = new Date()
    const time = now.toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', hour12:false })
    const newMsg = { from:'agent', text:reply.trim(), time }
    setAllMsgs(prev => ({
      ...prev,
      [active.id]: [...(prev[active.id] || []), newMsg],
    }))
    setTickets(prev => prev.map(t => t.id === active.id ? { ...t, msgs: t.msgs + 1 } : t))
    setReply('')
  }

  const currentMsgs = active ? (allMsgs[active.id] || []) : []

  return (
    <div className="dash-page page-in">

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'var(--space-md)' }}>
        <h1 className="page-title">Support</h1>
      </div>

      {/* Main shell — Gmail-style split */}
      <div className={`sup-shell${active ? ' sup-shell--open' : ''}`}>

        {/* Left — ticket list */}
        <div className="sup-list">
          {/* Toolbar */}
          <div className="sup-toolbar">
            <div style={{ position:'relative', flex:1 }}>
              <Search size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text3)' }}/>
              <input className="input" style={{ paddingLeft:32, height:34, fontSize:'var(--fs-xs)' }} placeholder="Search tickets…" value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
            <div className="view-tabs" style={{ margin:0 }}>
              {['all','open','in-progress','resolved'].map(f => (
                <button key={f} className={`vtab${filter===f?' active':''}`} style={{ padding:'5px 10px', fontSize:'var(--fs-xs)' }} onClick={()=>setFilter(f)}>
                  {f==='in-progress'?'Active':f.charAt(0).toUpperCase()+f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* List */}
          <div className="sup-list-body">
            {filtered.map(t => {
              const S = STATUS[t.status]
              const P = PRIORITY[t.priority]
              const isActive = active?.id === t.id
              return (
                <div key={t.id} className={`sup-list-item${isActive?' sup-list-item--active':''}`} onClick={()=>setActive(t)}>
                  <span style={{ width:8, height:8, borderRadius:'50%', background:P.dot, flexShrink:0, marginTop:4 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:6, marginBottom:3 }}>
                      <span style={{ fontSize:'var(--fs-xs)', fontWeight:700, color:isActive?'var(--accent-hover)':'var(--text1)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.subject}</span>
                      <span style={{ fontSize:10, color:'var(--text4)', flexShrink:0 }}>{t.date.split(',')[0]}</span>
                    </div>
                    <div style={{ fontSize:10, color:'var(--text3)', marginBottom:5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.user} · {t.email}</div>
                    <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                      <span className="badge badge-gold" style={{ fontSize:9, padding:'1px 6px' }}>{t.category}</span>
                      <span className={`badge ${S.cls}`} style={{ fontSize:9, padding:'1px 6px' }}>{S.l}</span>
                      <span className={`badge ${P.cls}`} style={{ fontSize:9, padding:'1px 6px' }}>{P.l}</span>
                    </div>
                  </div>
                  <ChevronRight size={14} color="var(--text4)" style={{ flexShrink:0, marginTop:2 }}/>
                </div>
              )
            })}
          </div>
        </div>

        {/* Right — ticket detail */}
        <div className="sup-detail">
          {active ? (
            <>
              {/* Detail header */}
              <div className="sup-detail-header">
                <button className="sup-back-btn" onClick={()=>setActive(null)}>
                  <ChevronRight size={15} style={{ transform:'rotate(180deg)' }}/> Back
                </button>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, color:'var(--text1)', fontSize:'var(--fs-md)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{active.subject}</div>
                  <div style={{ fontSize:'var(--fs-xs)', color:'var(--text3)', marginTop:2 }}>{active.id} · {active.date}</div>
                </div>
                <button className="btn btn-ghost btn-icon" style={{ width:28, height:28 }} onClick={()=>setActive(null)}><X size={14}/></button>
              </div>

              {/* Meta badges */}
              <div style={{ padding:'10px 18px', borderBottom:'1px solid var(--border-subtle)', display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                <span className={`badge ${STATUS[active.status].cls}`} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                  {(() => { const Icon = STATUS[active.status].icon; return <Icon size={10}/>; })()}
                  {STATUS[active.status].l}
                </span>
                <span className={`badge ${PRIORITY[active.priority].cls}`}>{PRIORITY[active.priority].l}</span>
                <span className="badge badge-gold"><Tag size={10}/> {active.category}</span>
                <select className="input" style={{ marginLeft:'auto', width:'auto', height:28, fontSize:'var(--fs-xs)', padding:'0 8px' }}
                  value={active.status} onChange={e=>updateStatus(active.id, e.target.value)}>
                  {Object.entries(STATUS).map(([k,v]) => <option key={k} value={k}>{v.l}</option>)}
                </select>
              </div>

              {/* User info */}
              <div style={{ padding:'12px 18px', borderBottom:'1px solid var(--border-subtle)', display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--bg-active)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'var(--fs-xs)', fontWeight:700, color:'var(--accent-hover)', flexShrink:0 }}>
                  {active.user.split(' ').map(n=>n[0]).join('')}
                </div>
                <div>
                  <div style={{ fontWeight:600, color:'var(--text1)', fontSize:'var(--fs-sm)' }}>{active.user}</div>
                  <div style={{ fontSize:'var(--fs-xs)', color:'var(--text3)' }}>{active.email}</div>
                </div>
                <div style={{ marginLeft:'auto', textAlign:'right' }}>
                  <div style={{ fontSize:'var(--fs-xs)', color:'var(--text3)' }}>Messages</div>
                  <div style={{ fontSize:'var(--fs-sm)', fontWeight:700, color:'var(--accent-hover)' }}>{active.msgs}</div>
                </div>
              </div>

              {/* Messages */}
              <div className="sup-messages">
                {currentMsgs.length === 0 && (
                  <div style={{ textAlign:'center', color:'var(--text4)', fontSize:'var(--fs-xs)', padding:'1rem' }}>No messages yet.</div>
                )}
                {currentMsgs.map((m, i) => (
                  <div key={i} className={`sup-msg sup-msg--${m.from}`}>
                    {m.from === 'user' && (
                      <div style={{ width:26, height:26, borderRadius:'50%', background:'var(--bg-active)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'var(--fs-xs)', fontWeight:700, color:'var(--accent-hover)', flexShrink:0 }}>
                        {active.user.split(' ').map(n=>n[0]).join('')}
                      </div>
                    )}
                    <div className={`sup-bubble sup-bubble--${m.from}`}>
                      <div style={{ fontSize:'var(--fs-xs)', lineHeight:1.6 }}>{m.text}</div>
                      <div style={{ fontSize:9, opacity:0.6, marginTop:4, textAlign:m.from==='agent'?'right':'left' }}>{m.time}</div>
                    </div>
                    {m.from === 'agent' && (
                      <div style={{ width:26, height:26, borderRadius:'50%', background:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'var(--fs-xs)', fontWeight:700, color:'#fff', flexShrink:0 }}>A</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Reply box */}
              <div className="sup-reply">
                <textarea
                  className="sup-reply-input"
                  placeholder="Type your reply…"
                  value={reply}
                  onChange={e=>setReply(e.target.value)}
                  onKeyDown={e=>{ if(e.key==='Enter' && (e.ctrlKey||e.metaKey)) sendReply() }}
                  rows={2}
                />
                <button className="btn btn-primary btn-sm" style={{ flexShrink:0 }} disabled={!reply.trim()} onClick={sendReply}>
                  <Send size={13}/> Send
                </button>
              </div>
            </>
          ) : (
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12, color:'var(--text4)' }}>
              <MessageCircle size={44} strokeWidth={1}/>
              <div style={{ fontSize:'var(--fs-sm)', fontWeight:500 }}>Select a ticket to view</div>
              <div style={{ fontSize:'var(--fs-xs)', color:'var(--text4)' }}>{filtered.length} tickets in this view</div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .dash-page { display:flex; flex-direction:column; gap:var(--space-lg); width:100%; }

        .sup-shell {
          display: grid;
          grid-template-columns: 340px 1fr;
          height: 80vh;
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          overflow: hidden;
          background: var(--bg-card);
          box-shadow: 0 20px 50px rgba(0,0,0,0.12);
        }

        .sup-list {
          display: flex; flex-direction: column;
          border-right: 1px solid var(--border-subtle);
          overflow: hidden;
          background: var(--bg-surface);
        }

        .sup-toolbar {
          display: flex; flex-direction: column; gap: 8px;
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-subtle);
          flex-shrink: 0;
        }

        .sup-list-body { flex:1; overflow-y:auto; }

        .sup-list-item {
          display: flex; align-items: flex-start; gap: 8px;
          padding: 12px 14px;
          cursor: pointer;
          border-bottom: 1px solid var(--border-subtle);
          border-left: 3px solid transparent;
          transition: background .15s, border-color .15s;
        }

        .sup-list-item:hover { background: var(--bg-hover); }

        .sup-list-item--active {
          background: var(--bg-active);
          border-left-color: var(--accent-hover);
        }

        .sup-detail {
          display: flex; flex-direction: column;
          overflow: hidden;
          background: var(--bg-raised);
        }

        .sup-detail-header {
          display: flex; align-items: center; gap: 10px;
          padding: 12px 18px;
          border-bottom: 1px solid var(--border-subtle);
          flex-shrink: 0;
        }

        .sup-back-btn {
          display: none; align-items: center; gap: 4px;
          background: none; border: none; cursor: pointer;
          color: var(--accent-hover); font-size: var(--fs-xs);
          font-weight: 600; font-family: inherit; padding: 4px 0;
        }

        .sup-messages {
          flex: 1; overflow-y: auto;
          padding: 16px 18px;
          display: flex; flex-direction: column; gap: 12px;
        }

        .sup-msg { display: flex; gap: 8px; align-items: flex-end; }
        .sup-msg--agent { flex-direction: row-reverse; }

        .sup-bubble {
          max-width: 75%;
          padding: 10px 14px;
          border-radius: 14px;
          font-family: var(--font-body);
        }

        .sup-bubble--user {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-bottom-left-radius: 4px;
          color: var(--text1);
        }

        .sup-bubble--agent {
          background: var(--accent);
          border-bottom-right-radius: 4px;
          color: #fff;
        }

        .sup-reply {
          display: flex; gap: 8px; align-items: flex-end;
          padding: 12px 16px;
          border-top: 1px solid var(--border-subtle);
          flex-shrink: 0;
        }

        .sup-reply-input {
          flex: 1;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          color: var(--text1);
          font-family: var(--font-body);
          font-size: var(--fs-sm);
          padding: 8px 12px;
          outline: none;
          resize: none;
          line-height: 1.5;
          transition: border-color .2s;
        }

        .sup-reply-input:focus { border-color: var(--accent); }
        .sup-reply-input::placeholder { color: var(--text3); }

        @media (max-width: 900px) {
          .sup-shell { grid-template-columns: 1fr; height: auto; }
          .sup-detail { display: none; min-height: 500px; }
          .sup-shell.sup-shell--open .sup-list { display: none; }
          .sup-shell.sup-shell--open .sup-detail { display: flex; }
          .sup-back-btn { display: flex !important; }
        }

        @media (max-width: 480px) {
          .sup-toolbar { gap: 6px; }
          .view-tabs .vtab { padding: 4px 8px; }
        }
      `}</style>
    </div>
  )
}